import QRCode from "qrcode";
import { useEffect, useState, type FormEvent } from "react";
import { Api, type Me } from "../api.js";
import { TextInput } from "../components/ui.js";
import { useI18n } from "../i18n.js";
import { AuthShell, errorText } from "./Screens.js";

/** After the password (or Google): the code from the authenticator app, or a recovery code. */
export function MfaChallenge({ api, me, onDone, onSignOut }: { api: Api; me: Me; onDone: (me: Me) => void; onSignOut: () => void }) {
  const { t } = useI18n();
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await (recovery ? api.mfaRecovery(code.trim()) : api.mfaVerify(code.trim()));
      onDone(await api.me());
    } catch (err) {
      setError(errorText(err, t("common.network")));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t("mfa.challengeTitle")} lead={t("mfa.challengeLead")}>
      <form onSubmit={submit} noValidate>
        <TextInput
          key={recovery ? "r" : "c"}
          label={recovery ? t("mfa.recoveryCode") : t("mfa.code")}
          value={code}
          onChange={setCode}
          autoComplete="one-time-code"
          required
          error={error ?? undefined}
        />
        <button className="btn primary wide" type="submit" disabled={busy || !code.trim()}>
          {t("mfa.verify")}
        </button>
      </form>
      <p>
        <button type="button" className="link" onClick={() => { setRecovery(!recovery); setCode(""); setError(null); }}>
          {recovery ? t("mfa.useApp") : t("mfa.useRecovery")}
        </button>
      </p>
      {me.mfa.recoveryCodesLeft > 0 && recovery && <p className="hint">{t("mfa.recoveryLeft", { n: me.mfa.recoveryCodesLeft })}</p>}
      <p>
        <button type="button" className="link" onClick={onSignOut}>
          {t("mfa.signOut")}
        </button>
      </p>
    </AuthShell>
  );
}

/** The set-up QR code, drawn here in the browser: the secret in it is never sent to any other service. */
function QrCode({ value, alt }: { value: string; alt: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toString(value, { type: "svg", margin: 2, errorCorrectionLevel: "M" }).then(
      (svg) => live && setSrc(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`),
      () => live && setSrc(null),
    );
    return () => {
      live = false;
    };
  }, [value]);
  return src ? <img className="qr" src={src} width={208} height={208} alt={alt} /> : null;
}

/** Set-up steps; used full-screen when required and inline in Account when a person chooses to turn it on. */
export function MfaSetup({ api, required, onDone, onCancel }: { api: Api; required: boolean; onDone: () => void; onCancel?: () => void }) {
  const { t } = useI18n();
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.mfaSetup().then(
      (s) => live && setSetup(s),
      (e) => live && setError(errorText(e, t("common.network"))),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.mfaVerify(code.trim());
      if (r.recoveryCodes) setCodes(r.recoveryCodes);
      else onDone();
    } catch (err) {
      setError(errorText(err, t("common.network")));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  if (codes) {
    return (
      <div className="card">
        <h2>{t("mfa.savedTitle")}</h2>
        <p>{t("mfa.savedLead")}</p>
        <pre className="code" tabIndex={0} aria-label={t("mfa.savedTitle")}>{codes.join("\n")}</pre>
        <button className="btn secondary" onClick={() => void navigator.clipboard?.writeText(codes.join("\n")).catch(() => undefined)}>
          {t("common.copy")}
        </button>
        <label className="inline">
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> {t("mfa.savedCheck")}
        </label>
        <button className="btn primary wide" disabled={!saved} onClick={onDone}>
          {t("mfa.done")}
        </button>
      </div>
    );
  }

  return (
    <form className="card" onSubmit={confirm} noValidate>
      <h2>{t("mfa.setupTitle")}</h2>
      <p>{required ? t("mfa.setupLeadRequired") : t("mfa.setupLead")}</p>
      <p>{t("mfa.step1")}</p>
      <p>{t("mfa.step2")}</p>
      {setup ? (
        <>
          <QrCode value={setup.otpauthUrl} alt={t("mfa.qrAlt")} />
          <p className="hint">
            <a href={setup.otpauthUrl}>{t("mfa.openApp")}</a>
          </p>
          <details>
            <summary>{t("mfa.noScan")}</summary>
            <p>{t("mfa.manual")}</p>
            <pre className="code" tabIndex={0} aria-label="Setup key">{setup.secret.replace(/(.{4})/g, "$1 ").trim()}</pre>
          </details>
        </>
      ) : (
        !error && <p>{t("common.loading")}</p>
      )}
      <TextInput label={t("mfa.step3")} value={code} onChange={setCode} autoComplete="one-time-code" error={error ?? undefined} />
      <div className="row">
        <button className="btn primary" type="submit" disabled={busy || !setup || !code.trim()}>
          {t("mfa.start")}
        </button>
        {onCancel && (
          <button className="btn ghost" type="button" onClick={onCancel}>
            {t("common.cancel")}
          </button>
        )}
      </div>
    </form>
  );
}

export function MfaSetupScreen({ api, onDone, onSignOut }: { api: Api; onDone: () => void; onSignOut: () => void }) {
  const { t } = useI18n();
  return (
    <AuthShell title={t("mfa.setupTitle")}>
      <MfaSetup api={api} required onDone={onDone} />
      <p>
        <button type="button" className="link" onClick={onSignOut}>
          {t("mfa.signOut")}
        </button>
      </p>
    </AuthShell>
  );
}
